import { useState, useEffect, useCallback, useRef } from 'react';

import { TOKEN_TYPES } from '#client/constants/tokens';
import useBreakpoints from '#client/hooks/useBreakpoints';
import useCardsContext from '#client/hooks/useCardsContext';
import useCustomerToken from '#client/hooks/useCustomerToken';
import useToastContext from '#client/hooks/useToastContext';

import { createVgsStyles, CopyButtonStyles } from '../components/VGS.styles';

import useCardInfo from './useCardInfo';
import useInitVGSShow from './useInitVGSShow';
import useTrackCardEvents from './useTrackCardEvents';

export default function useVGSShow() {
  const {
    sizes: { isDesktop }
  } = useBreakpoints();
  const [loadingCCV, setLoadingCCV] = useState(true);
  const { clickCopyTracker } = useTrackCardEvents();
  const [loadingCardNumber, setLoadingCardNumber] = useState(true);
  const refCCV = useRef(null);
  const refCardNumber = useRef(null);

  const { virtualCard } = useCardsContext();
  const { id } = useCardInfo();

  const { createCommonErrorToast } = useToastContext();

  const { getCustomerToken } = useCustomerToken({
    type: TOKEN_TYPES.CARDS_SENSITIVE
  });

  const { createVGSShow, scriptStatus } = useInitVGSShow();

  const showCVV = useCallback(
    ({ show, cardId, customerToken }) => {
      const cvv = show.request({
        name: 'data-text',
        method: 'GET',
        path: `/cards/${cardId}/secure-data/cvv2`,
        headers: {
          Authorization: `Bearer ${customerToken}`
        },
        htmlWrapper: 'text',
        jsonPathSelector: 'data.attributes.cvv2'
      });

      const copyButton = show.copyFrom(
        cvv,
        {
          text: ''
        },
        (copyStatus) => {
          if (copyStatus === 'success') {
            refCCV.current.showTooltip();
            clickCopyTracker();
          }
          if (copyStatus === 'error') {
            createCommonErrorToast({ message: 'Failed to copy CVV' });
          }
        }
      );

      cvv.on('requestSuccess', () => {
        setLoadingCCV(false);
        copyButton.render('#cvv2Copy', CopyButtonStyles);
      });

      cvv.on('requestFail', (error) => {
        setLoadingCCV(false);
        createCommonErrorToast({ error });
      });

      cvv.on('revealFail', (error) => {
        setLoadingCCV(false);
        createCommonErrorToast({ error });
        // hide cvv
        const cvvNode = document.getElementById('cvv2');
        if (cvvNode) {
          cvvNode.style.display = 'none';
        }
      });

      cvv.render('#cvv2', createVgsStyles(isDesktop));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createCommonErrorToast, isDesktop]
  );

  const showCardNumber = useCallback(
    ({ show, cardId, customerToken }) => {
      const cardNumber = show.request({
        name: 'data-text',
        method: 'GET',
        path: `/cards/${cardId}/secure-data/pan`,
        headers: {
          Authorization: `Bearer ${customerToken}`
        },
        serializers: [show.SERIALIZERS.replace('(\\d{4})(\\d{4})(\\d{4})(\\d{4})', '$1 $2 $3 $4')],
        htmlWrapper: 'text',
        jsonPathSelector: 'data.attributes.pan'
      });

      const copyButton = show.copyFrom(
        cardNumber,
        {
          text: ''
        },
        (copyStatus) => {
          if (copyStatus === 'success') {
            clickCopyTracker();
            refCardNumber.current.showTooltip();
          }
          if (copyStatus === 'error') {
            createCommonErrorToast({ message: 'Failed to copy CVV' });
          }
        }
      );

      cardNumber.on('requestSuccess', () => {
        setLoadingCardNumber(false);
        copyButton.render('#cardNumberCopy', CopyButtonStyles);
      });

      cardNumber.on('requestFail', (error) => {
        setLoadingCardNumber(false);
        createCommonErrorToast({ error });
      });

      cardNumber.on('revealFail', (error) => {
        setLoadingCardNumber(false);
        createCommonErrorToast({ error });
        // hide cardNumber
        const cardNumberNode = document.getElementById('cardNumber');
        if (cardNumberNode) {
          cardNumberNode.style.display = 'none';
        }
      });

      cardNumber.render('#cardNumber', createVgsStyles(isDesktop));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createCommonErrorToast, isDesktop]
  );

  const showVirtualCardInfo = useCallback(async () => {
    try {
      const showPromise = createVGSShow();
      const customerTokenPromise = getCustomerToken();
      const [show, customerToken] = await Promise.all([showPromise, customerTokenPromise]);

      showCVV({ show, cardId: id, customerToken });
      showCardNumber({ show, cardId: id, customerToken });
    } catch (error) {
      createCommonErrorToast({ error });
    }
  }, [createVGSShow, getCustomerToken, showCVV, id, showCardNumber, createCommonErrorToast]);

  useEffect(() => {
    if (scriptStatus === 'ready' && virtualCard) {
      showVirtualCardInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptStatus, virtualCard]);

  return {
    loadingCCV,
    loadingCardNumber,
    refCCV,
    refCardNumber
  };
}
